import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { drivers, type DriverRow } from "@/api/client";
import { DataTable, type DataCol } from "@/components/DataTable";
import { Drawer } from "@/components/Drawer";
import { KV } from "@/components/KV";
import { PageHead } from "@/components/PageHead";
import { StatGrid } from "@/components/StatGrid";
import { Select, SearchInput } from "@/components/inputs";
import { ago, dateTime, POLL } from "@/lib/format";
import { DRIVER_CHIP, DriverChip, DriverInfoPanel } from "@/modules/drivers/common";

import { Icon } from "@/components/icons";
/** Overview = the daily lookup surface: metrics + the estate table + a
 *  READ drawer (edits live on the Edit Driver page — ops is 90% lookups). */
export function DriversOverviewPage() {
  const nav = useNavigate();
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState<string | null>(null);

  const stats = useQuery({ queryKey: ["drivers", "stats"], queryFn: drivers.stats,
    refetchInterval: POLL.LIST });
  const q = useQuery({
    queryKey: ["drivers", "list", status, search],
    queryFn: () => drivers.list({
      status: status || undefined, search: search || undefined, limit: "500",
    }),
    refetchInterval: POLL.LIST,
  });
  const rows = q.data?.drivers ?? [];
  const detail = useQuery({
    queryKey: ["drivers", "detail", sel],
    queryFn: () => drivers.detail(sel!),
    enabled: !!sel,
  });

  const dim = <span style={{ color: "var(--t-dim)" }}>—</span>;
  const cols = useMemo<DataCol<DriverRow>[]>(() => [
    { key: "code", priority: 1, label: "ID", width: 120, mono: true, render: (d) => d.code },
    { key: "name", priority: 1, label: "Name", width: 180, render: (d) => d.name },
    { key: "msisdn", priority: 2, label: "Number", width: 140, mono: true, render: (d) => d.msisdn },
    { key: "status", priority: 2, label: "Status", width: 100, render: (d) => <DriverChip st={d.status} /> },
    { key: "invited_by", priority: 3, label: "Invited by", width: 120, mono: true, render: (d) => d.invited_by },
    { key: "invited", priority: 3, label: "Invited", width: 90, mono: true, render: (d) => ago(d.created_at), title: (d) => dateTime(d.created_at) },
    { key: "verified", priority: 3, label: "Verified", width: 90, mono: true, render: (d) => (d.verified_at ? ago(d.verified_at) : dim), title: (d) => (d.verified_at ? dateTime(d.verified_at) : undefined) },
    { key: "seen", priority: 2, label: "App activity", width: 100, mono: true, render: (d) => (d.last_app_seen ? ago(d.last_app_seen) : dim) },
  ], []);

  const st = stats.data;
  const d = detail.data;

  return (
    <div className="mpage on">
      <PageHead eyebrow="Drivers · Overview" title="Driver estate"
        desc="Platform-level identities: companies own assets, never drivers - a company's relationship with a driver is the assets allocated to them." />

      <StatGrid stats={[
        { k: "Total", v: st?.total ?? "—" },
        { k: "Invited (awaiting app)", v: st?.invited ?? "—" },
        { k: "Active", v: st?.active ?? "—" },
        { k: "Blocked", v: st?.blocked ?? "—" },
      ]} />

      <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
        <SearchInput placeholder="Search name / number / DRV id…" value={search}
          onChange={(e) => setSearch(e.target.value)} style={{ width: 260 }} />
        <Select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {Object.keys(DRIVER_CHIP).map((s) => <option key={s} value={s}>{s}</option>)}
        </Select>
      </div>
      <DataTable
        rows={rows}
        cols={cols}
        rowKey={(r) => r.driver_id}
        pageSize={100}
        onRowClick={(r) => setSel(r.driver_id)}
        empty={
          <div className="panel panel-pad cap">
            No drivers yet - add the first one on the New Driver page.
          </div>
        }
      />

      {sel && d && (
        <Drawer onClose={() => setSel(null)}
          header={<>
            <span style={{ fontWeight: 700 }}>{d.name}</span>
            <span className="mono cap">{d.code}</span>
            <DriverChip st={d.status} />
            <button className="btn ghost" style={{ marginLeft: 8 }}
              onClick={() => nav(`/drivers/edit?id=${d.driver_id}`)}>
              <Icon name="edit" /> Edit
            </button>
          </>}>
          {d.photo && (
            <img src={drivers.photoUrl(d.driver_id, d.photo.captured_at)} alt={d.name}
              style={{ width: 110, height: 110, objectFit: "cover", borderRadius: 10,
                border: "1px solid var(--line2)", marginBottom: 12 }} />
          )}
          <div className="section-label">Identity</div>
          <KV k="Driver ID" v={d.code} />
          <KV k="Number" v={d.msisdn} />
          <KV k="Invited by" v={d.invited_by} />
          <KV k="Invited" v={dateTime(d.created_at)} />
          <KV k="Verified" v={d.verified_at ? dateTime(d.verified_at) : "(app not yet bound)"} />
          {d.blocked_at && <KV k="Blocked" v={dateTime(d.blocked_at)} accent="var(--bad)" />}

          <div className="section-label tight">KYC</div>
          {d.kyc ? (
            <>
              <KV k="Driving licence" v={d.kyc.dl_number ?? "—"}
                accent={d.kyc.dl_verified_at ? "var(--ok)" : "var(--t-dim)"} />
              <KV k="Current addr" v={d.kyc.temp_address ? `${d.kyc.temp_address} · ${d.kyc.temp_pincode ?? ""}` : "—"} mono={false} />
              <KV k="Permanent addr" v={d.kyc.perm_address ? `${d.kyc.perm_address} · ${d.kyc.perm_pincode ?? ""}` : "—"} mono={false} />
            </>
          ) : (
            <div className="cap">No KYC on file - add it via Edit.</div>
          )}

          <DriverInfoPanel d={d} />
        </Drawer>
      )}
    </div>
  );
}
